import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { formatTime } from "@utils/timeGrid";

interface LunchOverlapModalProps {
  startHour: number;
  durationHours: number;
  lunchStart: number;
  lunchEnd: number;
  dayStart?: number;
  dayEnd?: number;
  onForce: () => void;
  onSplit: (before: { startHour: number; duration: number }, after: { startHour: number; duration: number }) => void;
  onCancel: () => void;
}

export default function LunchOverlapModal({
  startHour,
  durationHours,
  lunchStart,
  lunchEnd,
  dayStart,
  dayEnd,
  onForce,
  onSplit,
  onCancel,
}: LunchOverlapModalProps) {
  const endHour = startHour + durationHours;
  const lunchDuration = lunchEnd - lunchStart;

  // Split with top as reference: keep startHour, before lunch = startHour..lunchStart, after lunch = lunchEnd..remaining
  const topBeforeDuration = Math.max(0, lunchStart - startHour);
  const topAfterDuration = Math.max(0, durationHours - topBeforeDuration);
  const canSplitTop = topBeforeDuration > 0 && topAfterDuration > 0;

  // Split with bottom as reference: keep endHour, after lunch = lunchEnd..endHour, before lunch = (lunchStart - remaining)..lunchStart
  const botAfterDuration = Math.max(0, endHour - lunchEnd);
  const botBeforeDuration = Math.max(0, durationHours - botAfterDuration);
  const botBeforeStart = lunchStart - botBeforeDuration;
  const canSplitBot = botAfterDuration > 0 && botBeforeDuration > 0;

  // Une découpe qui commence avant ou finit après les horaires du compagnon n'est pas
  // une option : on la masque, sauf si c'est la seule qui reste.
  const topFits = dayEnd === undefined || lunchEnd + topAfterDuration <= dayEnd + 1e-6;
  const botFits = dayStart === undefined || botBeforeStart >= dayStart - 1e-6;
  const showTop = canSplitTop && (topFits || !(canSplitBot && botFits));
  // La découpe « réf. bas » avance le début du travail avant l'endroit où il a été posé
  // (11h00 → 9h45) et empiète sur le matin déjà chargé : on ne la propose qu'en repli,
  // quand la découpe « réf. haut » ne tient pas dans la journée du compagnon.
  const showBot = canSplitBot && !(canSplitTop && topFits) && (botFits || !showTop);

  // Check if both splits produce identical results
  const splitsIdentical = canSplitTop && canSplitBot
    && startHour === botBeforeStart
    && topBeforeDuration === botBeforeDuration
    && topAfterDuration === botAfterDuration;

  return (
    <BaseModal
      title="Ce créneau chevauche la pause déjeuner"
      onClose={onCancel}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="text-h3" style={{ fontFamily: "Poppins", color: "#525252" }}>
          Le travail ({formatTime(startHour)} — {formatTime(endHour)}, {durationHours}h) chevauche la pause déjeuner ({formatTime(lunchStart)} — {formatTime(lunchEnd)}).
        </div>

        {(showTop || showBot) && (
          <div className="flex flex-col gap-3">
            {/* Split top: only if not identical to bottom, or if it's the only option */}
            {showTop && (
              <div
                className="flex flex-col gap-2 p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                style={{ background: "#F0F4FF", border: "1px solid #C7D2FE" }}
                onClick={() => onSplit(
                  { startHour, duration: topBeforeDuration },
                  { startHour: lunchEnd, duration: topAfterDuration },
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="text-h3 font-medium" style={{ color: "#4338CA" }}>
                    {splitsIdentical || !showBot ? "Découper autour de la pause" : "Découper (réf. haut)"}
                  </div>
                </div>
                <div className="flex flex-row gap-3 items-center">
                  <div className="flex-1 p-2 rounded" style={{ background: "#E0E7FF" }}>
                    <div className="text-h4 font-medium" style={{ color: "#4338CA" }}>Partie 1</div>
                    <div className="text-h4" style={{ color: "#6366F1" }}>
                      {formatTime(startHour)} — {formatTime(lunchStart)} ({topBeforeDuration}h)
                    </div>
                  </div>
                  <div className="text-h4 text-neutral-400">Pause</div>
                  <div className="flex-1 p-2 rounded" style={{ background: "#E0E7FF" }}>
                    <div className="text-h4 font-medium" style={{ color: "#4338CA" }}>Partie 2</div>
                    <div className="text-h4" style={{ color: "#6366F1" }}>
                      {formatTime(lunchEnd)} — {formatTime(lunchEnd + topAfterDuration)} ({topAfterDuration}h)
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Split bottom: only show if different from top */}
            {showBot && !(splitsIdentical && showTop) && (
              <div
                className="flex flex-col gap-2 p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                style={{ background: "#F0FFF4", border: "1px solid #A7F3D0" }}
                onClick={() => onSplit(
                  { startHour: botBeforeStart, duration: botBeforeDuration },
                  { startHour: lunchEnd, duration: botAfterDuration },
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="text-h3 font-medium" style={{ color: "#065F46" }}>
                    {showTop ? "Découper (réf. bas)" : "Découper autour de la pause"}
                  </div>
                </div>
                <div className="flex flex-row gap-3 items-center">
                  <div className="flex-1 p-2 rounded" style={{ background: "#D1FAE5" }}>
                    <div className="text-h4 font-medium" style={{ color: "#065F46" }}>Partie 1</div>
                    <div className="text-h4" style={{ color: "#059669" }}>
                      {formatTime(botBeforeStart)} — {formatTime(lunchStart)} ({botBeforeDuration}h)
                    </div>
                  </div>
                  <div className="text-h4 text-neutral-400">Pause</div>
                  <div className="flex-1 p-2 rounded" style={{ background: "#D1FAE5" }}>
                    <div className="text-h4 font-medium" style={{ color: "#065F46" }}>Partie 2</div>
                    <div className="text-h4" style={{ color: "#059669" }}>
                      {formatTime(lunchEnd)} — {formatTime(endHour)} ({botAfterDuration}h)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onCancel}>
          Annuler
        </SecondaryButton>
        <SecondaryButton variant="small" onClick={onForce}>
          Forcer sur la pause
        </SecondaryButton>
      </div>
    </BaseModal>
  );
}
